import { DaySchedule, Reservation, SiteSettings } from '../types';
import { DEFAULT_OPENING_SCHEDULE } from '../data/imminghamData';

export interface DateAvailabilityStatus {
  date: Date;
  isoDate: string; // YYYY-MM-DD
  formattedDate: string; // e.g. "Wednesday, 12 August 2026"
  dayName: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  isAvailable: boolean;
  isOpenDay: boolean;
  isPast: boolean;
  isWithinProcessingPeriod: boolean;
  isBlocked: boolean;
  isHoliday: boolean;
  isFullyBooked: boolean;
  isLeadTimeRestricted: boolean;
  reason?: string;
  openTime?: string;
  closeTime?: string;
  currentBookingsCount: number;
  maxBookingsCount?: number;
}

export interface AvailabilityEngineOptions {
  siteSettings?: SiteSettings;
  existingReservations?: Reservation[];
  startDate?: Date; // Defaults to today
  bookingWindowDays?: number; // Defaults to 30 or siteSettings.bookingWindowDays
  minimumProcessingDays?: number; // Minimum processing days from settings
  leadTimeHours?: number; // Minimum lead time in hours
  currentReservationDate?: string;
}

const WEEKDAYS: ('Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday')[] = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

/**
 * Get configured minimum processing days from site settings
 */
export function getMinimumProcessingDays(siteSettings?: SiteSettings): number {
  if (
    siteSettings?.minimumProcessingDays !== undefined &&
    siteSettings.minimumProcessingDays !== null &&
    !isNaN(Number(siteSettings.minimumProcessingDays))
  ) {
    return Math.max(0, Number(siteSettings.minimumProcessingDays));
  }
  if (
    siteSettings?.leadTimeHours !== undefined &&
    siteSettings.leadTimeHours !== null &&
    !isNaN(Number(siteSettings.leadTimeHours)) &&
    siteSettings.leadTimeHours > 0
  ) {
    return Math.ceil(Number(siteSettings.leadTimeHours) / 24);
  }
  return 2; // Default system configuration
}

/**
 * Get weekday name for a Date
 */
export function getWeekdayName(date: Date): 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday' {
  return WEEKDAYS[date.getDay()] as any;
}

/**
 * Format Date to YYYY-MM-DD ISO string (local date component)
 */
export function toIsoDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format Date to standard human readable string e.g. "Wednesday, 12 August 2026"
 */
export function formatDateLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Helper to get opening schedule with fallback
 */
export function getEffectiveSchedule(siteSettings?: SiteSettings): DaySchedule[] {
  if (siteSettings?.openingHoursSchedule && siteSettings.openingHoursSchedule.length > 0) {
    return siteSettings.openingHoursSchedule;
  }
  return DEFAULT_OPENING_SCHEDULE;
}

/**
 * Central Availability Engine: Check availability of a single date
 */
export function checkDateAvailability(
  targetDate: Date,
  options: AvailabilityEngineOptions = {}
): DateAvailabilityStatus {
  const {
    siteSettings,
    existingReservations = [],
    startDate = new Date(),
    bookingWindowDays = siteSettings?.bookingWindowDays || 30,
    leadTimeHours = siteSettings?.leadTimeHours || 0
  } = options;

  const minProcessingDays =
    options.minimumProcessingDays !== undefined && options.minimumProcessingDays !== null
      ? Math.max(0, Number(options.minimumProcessingDays))
      : getMinimumProcessingDays(siteSettings);

  const dateCopy = new Date(targetDate);
  dateCopy.setHours(0, 0, 0, 0);

  const today = new Date(startDate);
  today.setHours(0, 0, 0, 0);

  // Earliest collection date calculated from configured processing period: Today + Minimum Processing Days
  const earliestCollectionDate = new Date(today);
  earliestCollectionDate.setDate(earliestCollectionDate.getDate() + minProcessingDays);

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + bookingWindowDays);

  const isoDate = toIsoDateString(dateCopy);
  const formattedDate = formatDateLabel(dateCopy);
  const dayName = getWeekdayName(dateCopy);

  // Find schedule entry from siteSettings (or fallback to default)
  const schedule = getEffectiveSchedule(siteSettings);
  const daySchedule = schedule.find((s) => s.day === dayName);

  // Check 1: Is past date
  const isPast = dateCopy < today;

  // Check 2: Minimum Processing Period check (today or within configured processing period)
  const isWithinProcessingPeriod = !isPast && dateCopy < earliestCollectionDate;

  // Check 3: Lead time restriction
  let isLeadTimeRestricted = false;
  if (leadTimeHours > 0) {
    const minBookingTime = new Date(startDate.getTime() + leadTimeHours * 60 * 60 * 1000);
    if (dateCopy < minBookingTime) {
      isLeadTimeRestricted = true;
    }
  }

  // Check 4: Is open day in schedule
  const isOpenDay = !!(daySchedule && daySchedule.isOpen);

  // Check 5: Manually blocked dates
  const blockedDates = siteSettings?.blockedDates || [];
  const isBlocked = blockedDates.includes(isoDate) || blockedDates.includes(formattedDate);

  // Check 6: Holiday closures
  const holidayDates = siteSettings?.holidayDates || [];
  const isHoliday = holidayDates.includes(isoDate) || holidayDates.includes(formattedDate);

  // Check 7: Existing bookings count and max capacity check
  const activeReservationsOnDate = existingReservations.filter((r) => {
    if (r.status === 'Cancelled' || r.status === 'Cancelled by Customer') return false;
    const pref = r.preferredCollectionDay;
    if (!pref) return false;
    return pref === formattedDate || pref === isoDate || pref.includes(isoDate);
  });
  const currentBookingsCount = activeReservationsOnDate.length;
  const maxBookingsCount = siteSettings?.maxBookingsPerDay;
  const isFullyBooked = maxBookingsCount ? maxBookingsCount > 0 && currentBookingsCount >= maxBookingsCount : false;

  // Determine overall availability and explicit reason
  let isAvailable = true;
  let reason = '';

  if (isPast) {
    isAvailable = false;
    reason = 'Past date';
  } else if (isWithinProcessingPeriod) {
    isAvailable = false;
    reason =
      minProcessingDays === 0
        ? 'Not selectable'
        : dateCopy.getTime() === today.getTime()
        ? `Same-day collection unavailable (minimum ${minProcessingDays} day${minProcessingDays === 1 ? '' : 's'} processing time)`
        : `Within configured ${minProcessingDays}-day processing window`;
  } else if (dateCopy > maxDate) {
    isAvailable = false;
    reason = `Outside booking window (${bookingWindowDays} days)`;
  } else if (isLeadTimeRestricted) {
    isAvailable = false;
    reason = `Requires at least ${leadTimeHours} hours advance notice`;
  } else if (!isOpenDay) {
    isAvailable = false;
    reason = `Hub is closed on ${dayName}s`;
  } else if (isBlocked) {
    isAvailable = false;
    reason = 'Date blocked by Hub Administration';
  } else if (isHoliday) {
    isAvailable = false;
    reason = 'Hub closed for holiday';
  } else if (isFullyBooked) {
    isAvailable = false;
    reason = `Fully booked (${currentBookingsCount}/${maxBookingsCount} capacity filled)`;
  }

  return {
    date: dateCopy,
    isoDate,
    formattedDate,
    dayName,
    isAvailable,
    isOpenDay,
    isPast,
    isWithinProcessingPeriod,
    isBlocked,
    isHoliday,
    isFullyBooked,
    isLeadTimeRestricted,
    reason,
    openTime: daySchedule?.openTime,
    closeTime: daySchedule?.closeTime,
    currentBookingsCount,
    maxBookingsCount
  };
}

/**
 * Automatically calculate Earliest Collection Date = Today + Minimum Processing Days.
 * If that calculated date is unavailable because the collection service is closed (or holiday/blocked),
 * automatically moves to the next available collection day supplied by the backend engine.
 */
export function getEarliestAvailableCollectionDate(
  options: AvailabilityEngineOptions = {}
): DateAvailabilityStatus | null {
  const {
    siteSettings,
    startDate = new Date(),
    bookingWindowDays = siteSettings?.bookingWindowDays || 30
  } = options;

  const minProcessingDays =
    options.minimumProcessingDays !== undefined && options.minimumProcessingDays !== null
      ? Math.max(0, Number(options.minimumProcessingDays))
      : getMinimumProcessingDays(siteSettings);

  const today = new Date(startDate);
  today.setHours(0, 0, 0, 0);

  // Start from Today + Minimum Processing Days
  const candidate = new Date(today);
  candidate.setDate(candidate.getDate() + minProcessingDays);

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + bookingWindowDays);

  while (candidate <= maxDate) {
    const status = checkDateAvailability(candidate, options);
    if (status.isAvailable) {
      return status;
    }
    // Advance to next day
    candidate.setDate(candidate.getDate() + 1);
  }

  return null;
}

/**
 * Get array of all collection date statuses in booking window + print required debug logs
 */
export function getAvailableCollectionDates(options: AvailabilityEngineOptions = {}): DateAvailabilityStatus[] {
  const {
    siteSettings,
    startDate = new Date(),
    bookingWindowDays = siteSettings?.bookingWindowDays || 30
  } = options;

  const minProcessingDays =
    options.minimumProcessingDays !== undefined && options.minimumProcessingDays !== null
      ? Math.max(0, Number(options.minimumProcessingDays))
      : getMinimumProcessingDays(siteSettings);

  const today = new Date(startDate);
  today.setHours(0, 0, 0, 0);

  const schedule = getEffectiveSchedule(siteSettings);
  const results: DateAvailabilityStatus[] = [];

  for (let i = 0; i <= bookingWindowDays; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const status = checkDateAvailability(d, options);
    results.push(status);
  }

  const earliestAvailable = getEarliestAvailableCollectionDate(options);
  const openDaysLoaded = schedule.filter((s) => s.isOpen).map((s) => `${s.day} (${s.openTime}-${s.closeTime})`);
  const availableSlots = results.filter((r) => r.isAvailable).map((r) => r.formattedDate);
  const fullyBooked = results.filter((r) => r.isFullyBooked).map((r) => r.formattedDate);

  if (import.meta.env.DEV) {
    console.log('=== UNIFORM LOOP AVAILABILITY ENGINE LOGS ===');
    console.log('Minimum Processing Days configured:', minProcessingDays);
    console.log('Opening days loaded:', openDaysLoaded);
    console.log('Earliest available collection date calculated:', earliestAvailable?.formattedDate || 'None found');
    console.log('Available booking dates generated:', availableSlots);
    console.log('Blocked dates:', siteSettings?.blockedDates || []);
    console.log('Fully booked dates:', fullyBooked);
    console.log('Current reservation date:', options.currentReservationDate || 'N/A (New Reservation)');
    console.log('Selectable dates returned:', availableSlots);
    console.log('===============================================');
  }

  return results;
}

/**
 * Returns human readable summary of enabled weekdays and times
 */
export function getEnabledDaysSummary(siteSettings?: SiteSettings): string {
  const schedule = getEffectiveSchedule(siteSettings);
  const openDays = schedule.filter((s) => s.isOpen);

  if (openDays.length === 0) {
    return 'Currently closed for collections';
  }

  const dayPlurals: Record<string, string> = {
    Monday: 'Mondays',
    Tuesday: 'Tuesdays',
    Wednesday: 'Wednesdays',
    Thursday: 'Thursdays',
    Friday: 'Fridays',
    Saturday: 'Saturdays',
    Sunday: 'Sundays'
  };

  const parts = openDays.map((d) => `${dayPlurals[d.day] || d.day} (${d.openTime}–${d.closeTime})`);
  return parts.join(' | ');
}
